import { useState } from 'react'

import { formatClock } from '@krp/client-runtime'
import { Button } from '@krp/ui-kit'

import { linkState } from '../model/link'
import type { EquipmentSnapshot, HardwareActions, HardwareLinks, HardwareStatus } from '../model/types'
import { Mark } from './Mark'

/**
 * Полоса «Связь с агентом» (п. 2.5, 3.1, 3.6): значок, слово, текст и действие по состоянию. Над табами всегда.
 * Потеряна связь — последние известные данные остаются на экране серыми с пометкой «на HH:MM».
 */
export function LinkStrip({
  snapshot,
  links,
  status,
  actions,
}: {
  snapshot: EquipmentSnapshot
  links: HardwareLinks
  status: HardwareStatus
  actions: HardwareActions
}) {
  const vm = linkState(snapshot.link, snapshot.now)
  const [details, setDetails] = useState(false)
  const [help, setHelp] = useState(false)
  const checking = status.checking
  const agent = vm.agent
  const host = agent?.hello.host ?? null
  const started = formatClock(host?.startedAt ?? null)

  return (
    <section aria-label="Связь с агентом" className={`hw-link hw-box hw-box--${vm.stage}`} data-phase={vm.phase}>
      <p role="status" className="hw-link__head">
        <Mark glyph={vm.glyph} word={vm.word} tone={vm.stage} /> <span>{vm.text}</span>
        {vm.staleNote !== null && <span className="hw-link__stale"> ({vm.staleNote})</span>}
      </p>
      {vm.warning !== null && <p className="hw-link__warning">{vm.warning}</p>}

      {vm.phase === 'denied' && vm.siteOrigin !== null && (
        <p>
          Адрес этого сайта: <code>{vm.siteOrigin}</code>
        </p>
      )}

      {vm.phase === 'choose' && (
        <ul className="hw-list" aria-label="Найденные программы-драйверы">
          {vm.options.map((option) => (
            <li key={option.port} className="hw-list__row">
              <span>
                порт {option.port} · {option.version}
                {option.modeWord !== null && <> · {option.modeWord}</>}
                {option.startedAt !== null && <> · запущена {option.startedAt}</>}
                {option.selected && <strong> · выбрана</strong>}
              </span>
              <Button
                variant="secondary"
                actionId="platform.hardware.agent-pick"
                disabled={option.selected || checking}
                disabledReason={option.selected ? 'Эта программа уже выбрана' : checking ? 'Подождите' : null}
                onClick={() => actions.pickAgent(option.port)}
              >
                Использовать
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="hw-buttons">
        {vm.phase === 'missing' &&
          (links.agentInstaller !== null ? (
            <Button variant="primary" actionId="platform.hardware.download-agent" onClick={actions.downloadAgent}>
              Скачать программу-драйвер
            </Button>
          ) : (
            <span className="hw-hint">Ссылка не задана — файл выдаёт администратор.</span>
          ))}
        {vm.phase === 'outdated' && links.agentInstaller !== null && (
          <Button variant="primary" actionId="platform.hardware.update-agent" onClick={actions.downloadAgent}>
            Скачать новую версию
          </Button>
        )}
        {vm.phase === 'denied' && (
          <Button variant="primary" actionId="platform.hardware.copy-origin" onClick={actions.copyOrigin}>
            Скопировать адрес сайта
          </Button>
        )}
        <Button
          variant="secondary"
          actionId="platform.hardware.recheck"
          disabled={checking}
          disabledReason={checking ? 'Идёт проверка' : null}
          onClick={actions.recheck}
        >
          Проверить сейчас
        </Button>
        {vm.phase === 'connected' && agent !== null && (
          <Button variant="ghost" actionId="platform.hardware.toggle" aria-expanded={details} onClick={() => setDetails((value) => !value)}>
            {details ? 'Скрыть сведения' : 'Сведения'}
          </Button>
        )}
        {vm.phase === 'missing' && (
          <Button variant="ghost" actionId="platform.hardware.toggle" aria-expanded={help} onClick={() => setHelp((value) => !value)}>
            Агент установлен, но не находится?
          </Button>
        )}
      </div>

      {vm.phase === 'missing' && !status.windows && <p className="hw-hint">Программа-драйвер работает только на Windows.</p>}

      {vm.phase === 'missing' && help && (
        <ul className="hw-list" aria-label="Что проверить">
          {vm.troubleshooting.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}

      {details && agent !== null && (
        <dl className="hw-details" aria-label="Сведения об агенте">
          <dt>Версия</dt>
          <dd>{agent.hello.version}</dd>
          <dt>Порт</dt>
          <dd>{agent.port}</dd>
          <dt>Как запущен</dt>
          <dd>{host?.mode === 'service' ? 'служба' : host?.mode === 'console' ? 'вручную' : 'неизвестно'}</dd>
          {started !== null && (
            <>
              <dt>Запущен</dt>
              <dd>{started}</dd>
            </>
          )}
          {host?.machine != null && (
            <>
              <dt>Компьютер</dt>
              <dd>{host.machine}</dd>
            </>
          )}
          <dt>Уровень возможностей</dt>
          <dd>{agent.hello.api?.level ?? 0}</dd>
          {agent.hello.instanceId.length > 0 && (
            <>
              <dt>Идентификатор</dt>
              <dd>{agent.hello.instanceId}</dd>
            </>
          )}
        </dl>
      )}
    </section>
  )
}
